import { formatLanguageString } from '@itslearning/atlas/strings/format';

/** Timespan interval in seconds  */
export const dateIntervals = {
    date: 5184000,
    day: 86400,
    hour: 3600,
    minute: 60
};

/**
 * Format date to relative to current moment
 * @param {Date} date - date that should be formatted
 * @param {String} locale - locale 'en-US'
 * @param {String} timeZone - time zone like 'Europe/Berlin'
 * @param {Boolean | undefined} twelveHourFormat - support AM/PM
 * @param {Object} i18n - language terms
 */
export function formatDate(date, locale, timeZone, twelveHourFormat, i18n) {
    const seconds = getSeconds(date);

    const intervalType = getIntervalType(date);
    const options = {};

    if (timeZone) {
        options.timeZone = timeZone;
    }

    if (twelveHourFormat || twelveHourFormat === false) {
        options.hour12 = twelveHourFormat;
    }

    let days, hours, minutes;

    switch (intervalType) {
        case 'date':
            options.year = 'numeric';
            options.month = '2-digit';
            options.day = '2-digit';

            return date.toLocaleDateString(locale, options);
        case 'day':
            days = Math.floor(seconds / dateIntervals.day);

            return formatLanguageString(i18n.nDaysAgo, days.toString());
        case 'week':
            return formatLanguageString(i18n.week, i18n[`day${date.getDay()}`], date.toLocaleTimeString(locale, {
                ...options,
                hour: '2-digit',
                minute: '2-digit'
            }));
        case 'yesterday':
            return formatLanguageString(i18n.oneDayAgo, date.toLocaleTimeString(locale, {
                ...options,
                hour: '2-digit',
                minute: '2-digit'
            }));
        case 'hour':
            hours = Math.floor(seconds / dateIntervals.hour);

            return hours === 1 ? i18n.oneHourAgo : formatLanguageString(i18n.nHoursAgo, hours.toString());
        case 'minute':
            minutes = Math.floor(seconds / dateIntervals.minute);

            return minutes === 1 ? i18n.oneMinuteAgo : formatLanguageString(i18n.nMinutesAgo, minutes.toString());
        default:
            return i18n.justNow;
    }
}

/**
 * Get seconds between date and now
 * @param {Date} date
 * @returns {Number}
 */
function getSeconds(date) {
    const now = new Date();

    return Math.floor((now.getTime() - date.getTime()) / 1000);
}

/**
 * Get interval type that should be displayed for relative format
 * @param {Date} date
 * @returns {IntervalType}
 */
export function getIntervalType(date) {
    const seconds = getSeconds(date);

    if (seconds / dateIntervals.date >= 1) {
        return 'date';
    }

    if (seconds / dateIntervals.day >= 1 && isYesterday(date)) {
        return 'yesterday';
    }

    if (seconds / dateIntervals.day >= 1 && isLessThanAWeekAGo(date)) {
        return 'week';
    }

    if (seconds / dateIntervals.day >= 1) {
        return 'day';
    }

    if (seconds / dateIntervals.hour >= 1) {
        return 'hour';
    }

    if (seconds / dateIntervals.minute >= 1) {
        return 'minute';
    }

    return 'second';
}

/**
 * Check if date is in a current week
 * @param {Date} date
 * @returns {Boolean}
 */
export function isDateInThisWeek(date) {
    const todayObj = new Date();
    const todayDate = todayObj.getDate();
    const todayDay = todayObj.getDay();

    // get first date of week
    const firstDayOfWeek = new Date(todayObj.setDate(todayDate - todayDay));

    // get last date of week
    const lastDayOfWeek = new Date(firstDayOfWeek);

    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

    // if date is equal or within the first and last dates of the week
    return date >= firstDayOfWeek && date <= lastDayOfWeek;
}

/**
 * Check if date is less than a week a go (6 days since)
 * @param {Date} date
 * @returns {Boolean}
 */
export function isLessThanAWeekAGo(date) {
    const dateObj = new Date();

    return date > dateObj - (1000 * 60 * 60 * 24 * 7);
}

/**
 * Check if date was yesterday
 * @param {Date} date
 * @returns {Boolean}
 */
export function isYesterday(date) {
    const yesterdayObj = new Date();

    yesterdayObj.setDate(yesterdayObj.getDate() - 1);
    const yesterdayMorning = new Date(yesterdayObj.getFullYear(), yesterdayObj.getMonth(), yesterdayObj.getDate(), 0, 0, 1);
    const yesterdayNight = new Date(yesterdayObj.getFullYear(), yesterdayObj.getMonth(), yesterdayObj.getDate(), 23, 59, 59);
    const yesterday = date >= yesterdayMorning && date <= yesterdayNight;

    return yesterday;
}

/**
 * Interval type definition
 * @typedef {('date'|'day'|'hour'|'minute'|'week'|'second'|'yesterday')} IntervalType
 */
